import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';

import { Grid, Section, Stack, Help } from '@gogame/components';
import { FormField, withForm, SidePageV2, FormFieldGroup } from '@gogame/form';

import {
  loadUsers,
  loadActiveUsers,
  loadBusinessPartner,
  loadBusinessPartners,
  loadActiveBusinessPartners,
  loadExpenseTypes,
  loadTrafficTypes,
  loadChannels,
  loadSpendSources,
} from 'scenes/Affiliates/sources/fields';
import { useIsAllowed } from '@gogame/permissions';

import AdditionalFeePeriods from './AdditionalFeePeriods';
import MediaSource from './MediaSource';
import {
  NAME,
  OWNER,
  BUSINESS_PARTNER,
  EXPENSE_TYPE,
  ADDITIONAL_FEE,
  REVENUE_SHARE,
  TRAFFIC_TYPES,
  CHANNEL,
  CONTACT_PERSON,
  FIRST_NAME,
  LAST_NAME,
  EMAIL,
  MOBILE,
  PHONE,
  SKYPE,
  DEPENDENCIES,
  EXPENSE_TYPE_REV_SHARE,
  MEDIA_SOURCE,
  SPEND_SOURCE,
  NETWORK_MOBILE,
  NETWORK_WEB,
  DYNAMIC_REWARDS_SUPPORT,
  DYNAMIC_REWARDS_SUPPORT_PERMISSION,
} from './constants';
import { AffiliateFormValidationSchema } from './schemas';
import { InvoiceProcessingRuleSection } from './InvoiceProcessingRuleSection';

const isOwnerBlocked = ({ isBlocked }) => isBlocked;

const AffiliateForm = withForm({
  validationSchema: AffiliateFormValidationSchema,
  enableReinitialize: true,
})(({
  children,
  title,
  subTitle,
  loading,
  onClose,
  values,
  initialValues,
  setFieldValue,
  isEdit,
  onExited,
  open,
  entityId,
}) => {
  const isAllowed = useIsAllowed();
  const {
    [NAME]: name,
    [BUSINESS_PARTNER]: businessPartnerValue,
    [MEDIA_SOURCE]: mediaSource,
  } = values;

  const { [MEDIA_SOURCE]: initialMediaSource } = initialValues;

  const [bpNetwork, setBPNetwork] = useState(null);

  const loadOnChangeBusinessPartner = useCallback(
    async ({ id }) => {
      const { contactPerson, defaultSpendSource, network } =
        await loadBusinessPartner({ id });

      setFieldValue(CONTACT_PERSON, contactPerson);

      if (defaultSpendSource !== undefined) {
        setFieldValue(SPEND_SOURCE, defaultSpendSource);
      }

      if (network === NETWORK_MOBILE) {
        setFieldValue(MEDIA_SOURCE, mediaSource || {});
      }

      if (network === NETWORK_WEB) {
        setFieldValue(MEDIA_SOURCE, mediaSource || { name: '' });
      }

      setBPNetwork(network);
    },
    [setFieldValue, mediaSource]
  );

  useEffect(() => {
    if (businessPartnerValue) {
      loadOnChangeBusinessPartner({ id: businessPartnerValue });
    }
  }, [businessPartnerValue]);

  const isRevShareExpenseType = useMemo(() => {
    return values[EXPENSE_TYPE] === EXPENSE_TYPE_REV_SHARE;
  }, [values[EXPENSE_TYPE]]);

  //@todo move clear to form strategy
  useEffect(() => {
    if (!isRevShareExpenseType) {
      setFieldValue(REVENUE_SHARE, undefined);
    }
  }, [isRevShareExpenseType]);

  const customSourceHelperText = useMemo(() => {
    return bpNetwork === NETWORK_WEB && name
      ? `Suggested: ${name.toLowerCase().replace(/ /g, '_')}_int`
      : null;
  }, [bpNetwork, name]);

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <SidePageV2.Title
        intensity={400}
        title={title}
        subTitle={subTitle}
        entityId={entityId}
      />
      <SidePageV2.NewBody>
        <Section title={'General'}>
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <FormField.TextField
                label={'Name'}
                required={true}
                padding={'0'}
                name={NAME}
                placeholder={'Commercial or brand name'}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Owner'}
                padding={'0'}
                name={OWNER}
                required={true}
                source={isEdit ? loadUsers : loadActiveUsers}
                lazy={true}
                isOptionDisabled={isOwnerBlocked}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Business partner'}
                padding={'0'}
                name={BUSINESS_PARTNER}
                source={
                  isEdit ? loadBusinessPartners : loadActiveBusinessPartners
                }
                lazy={true}
                required={true}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.MultiSelect
                label={'Traffic types'}
                padding={'0'}
                name={TRAFFIC_TYPES}
                source={loadTrafficTypes}
                lazy={true}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Channel'}
                padding={'0'}
                name={CHANNEL}
                source={loadChannels}
                lazy={true}
                disabled={isEdit}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Select
                label={'Expense type'}
                padding={'0'}
                name={EXPENSE_TYPE}
                depends={DEPENDENCIES[EXPENSE_TYPE]}
                source={loadExpenseTypes}
                lazy={true}
                required={true}
              />
            </Grid>
            {isRevShareExpenseType ? (
              <Grid item xs={4}>
                <FormField.TextField
                  type={'percent'}
                  label={'Revenue Share'}
                  padding={'0'}
                  required={true}
                  name={REVENUE_SHARE}
                />
              </Grid>
            ) : null}
            <Grid item xs={4}>
              <FormField.Select
                label={'Spend source'}
                name={SPEND_SOURCE}
                source={loadSpendSources}
                required={true}
                padding={'0'}
              />
            </Grid>
            {mediaSource ? (
              <MediaSource
                value={mediaSource}
                initialValue={initialMediaSource}
                customSourceHelperText={customSourceHelperText}
                required={bpNetwork === NETWORK_MOBILE}
              />
            ) : null}
            <Grid item xs={4}>
              <FormField.Switch
                name={DYNAMIC_REWARDS_SUPPORT}
                label={
                  <Stack as={'span'} direction={'row'} spacing={0.5}>
                    <span>Dynamic Rewards</span>
                    <Help>
                      The dynamic rewards feature is supported only for specific
                      affiliate. Please contact your CSM for more information.
                    </Help>
                  </Stack>
                }
                basis={0}
                padding={0}
                disabled={!isAllowed(DYNAMIC_REWARDS_SUPPORT_PERMISSION)}
              />
            </Grid>
          </Grid>
        </Section>
        <Section title={'Contact Person'}>
          <FormFieldGroup name={CONTACT_PERSON}>
            <Grid container spacing={3}>
              <Grid item xs={4}>
                <FormField.TextField
                  label={'First name'}
                  padding={'0'}
                  name={FIRST_NAME}
                />
              </Grid>
              <Grid item xs={4}>
                <FormField.TextField
                  label={'Last name'}
                  padding={'0'}
                  name={LAST_NAME}
                />
              </Grid>
              <Grid item xs={4}>
                <FormField.TextField
                  label={'Email'}
                  padding={'0'}
                  name={EMAIL}
                />
              </Grid>
              <Grid item xs={4}>
                <FormField.TextField
                  label={'Mobile'}
                  padding={'0'}
                  name={MOBILE}
                />
              </Grid>
              <Grid item xs={4}>
                <FormField.TextField
                  label={'Phone'}
                  padding={'0'}
                  name={PHONE}
                />
              </Grid>
              <Grid item xs={4}>
                <FormField.TextField
                  label={'Skype'}
                  padding={'0'}
                  name={SKYPE}
                />
              </Grid>
            </Grid>
          </FormFieldGroup>
        </Section>
        <Section
          title={'Additional Spend Fee'}
          subTitle={
            'Percent by which spend received via API should be increased, e.g. Agency Fee'
          }
        >
          <Grid container spacing={3}>
            <Grid item xs={9}>
              <AdditionalFeePeriods name={ADDITIONAL_FEE} />
            </Grid>
          </Grid>
        </Section>
        <InvoiceProcessingRuleSection
          active={businessPartnerValue !== undefined}
        />
      </SidePageV2.NewBody>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
      {children}
    </SidePageV2>
  );
});

export default AffiliateForm;
